import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ArrowRight } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { money } from '@/lib/money'
import type { Order, Paginated } from '@/types'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow } from '@/components/ui/Table'
import LogoLoader from '@/components/ui/LogoLoader'
import UrgentBadge from '@/components/job/UrgentBadge'
import PaymentBadge from '@/components/order/PaymentBadge'

export default function Orders() {
  const { data, isLoading } = useQuery({
    queryKey: ['orders', 'all'],
    queryFn: async () => (await api.get<Paginated<Order>>('/orders')).data,
  })

  if (isLoading) return <LogoLoader />

  return (
    <div>
      <PageHeader
        title="My orders"
        subtitle="Track every order and its production stage"
        actions={
          <Link to="/products">
            <Button>
              Browse products <ArrowRight className="size-4" />
            </Button>
          </Link>
        }
      />
      <Table columns={['Order', 'Job code', 'Status', 'Current stage', 'Production', 'Delivery due', 'Total']}>
        {(data?.data.length ?? 0) === 0 && (
          <EmptyRow colSpan={7}>
            <div className="flex flex-col items-center gap-3">
              <p>No orders yet - pick a product and place your first one.</p>
              <Link to="/products">
                <Button>
                  Browse products <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </EmptyRow>
        )}
        {data?.data.map((order) => (
          <Row key={order.id}>
            <Cell>
              <Link to={`/account/orders/${order.id}`} className="font-semibold text-brand-600 hover:underline">
                {order.number}
              </Link>
              <UrgentBadge urgent={order.is_urgent} variant="storefront" className="ml-1.5" />
              <p className="text-2xs text-muted">Ordered {formatDate(order.created_at)}</p>
            </Cell>
            <Cell className="font-mono text-xs">{order.job?.code}</Cell>
            <Cell>
              <StateBadge state={order.status} />
              {['unpaid', 'refund_due', 'refunded'].includes(order.payment_status ?? '') && (
                <PaymentBadge status={order.payment_status} className="ml-1" />
              )}
            </Cell>
            <Cell>
              {order.job?.status === 'queued'
                ? 'Waiting to start'
                : order.job?.current_department?.name ?? (order.status === 'completed' ? 'Done' : '-')}
            </Cell>
            <Cell>{formatDate(order.production_date)}</Cell>
            <Cell>{formatDate(order.expected_completion_date)}</Cell>
            <Cell className="font-semibold">{money(order.total)}</Cell>
          </Row>
        ))}
      </Table>
    </div>
  )
}
